import { supabase } from '../../lib/supabase'

/**
 * The accounts an administrator manages, read from muster.profiles (an
 * administrator reads every account, profiles_read_administrator) and
 * changed only through v1-create-account, v1-update-account, and
 * v1-set-account-active, which check the caller is an active administrator
 * and write each change with its audit event.
 */
export type AccountRole = 'agent' | 'supervisor' | 'leadership' | 'administrator'

export const ACCOUNT_ROLES: { key: AccountRole; label: string }[] = [
  { key: 'agent', label: 'Agent' },
  { key: 'supervisor', label: 'Supervisor' },
  { key: 'leadership', label: 'Leadership' },
  { key: 'administrator', label: 'Administrator' },
]

export function roleLabel(role: AccountRole): string {
  return ACCOUNT_ROLES.find((option) => option.key === role)?.label ?? role
}

export type Account = {
  id: string
  firstName: string
  lastName: string
  email: string
  role: AccountRole
  active: boolean
}

type ProfileRow = {
  id: string
  first_name: string
  last_name: string
  email: string
  role: AccountRole
  active: boolean
}

export async function getAccounts(): Promise<Account[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, first_name, last_name, email, role, active')
    .order('last_name', { ascending: true })
  if (error || !data) return []
  return (data as unknown as ProfileRow[]).map((row) => ({
    id: row.id,
    firstName: row.first_name,
    lastName: row.last_name,
    email: row.email,
    role: row.role,
    active: row.active,
  }))
}

// supervisorId is null for a team nobody leads yet, the only kind a new
// supervisor account can be given.
export type AccountTeam = {
  id: string
  name: string
  supervisorId: string | null
  fieldOfficeId?: string
  // The dates the team exists: an employee is placed on it only within them.
  openDate?: string
  closeDate?: string | null
}

// The teams an account can be given: any team not yet closed.
export async function getOpenTeams(): Promise<AccountTeam[]> {
  const today = new Date().toISOString().slice(0, 10)
  const { data, error } = await supabase
    .from('teams')
    .select('id, name, supervisor_id, field_office_id, open_date, close_date')
    .or(`close_date.is.null,close_date.gte.${today}`)
    .order('name', { ascending: true })
  if (error || !data) return []
  return (data as unknown as { id: string; name: string; supervisor_id: string | null; field_office_id?: string; open_date?: string; close_date?: string | null }[]).map((team) => ({
    id: team.id,
    name: team.name,
    supervisorId: team.supervisor_id,
    fieldOfficeId: team.field_office_id,
    openDate: team.open_date,
    closeDate: team.close_date ?? null,
  }))
}

export type NewAccount = {
  email: string
  password: string
  firstName: string
  lastName: string
  role: AccountRole
  // The teams a supervisor account leads.
  leadTeamIds?: string[]
  employee?: {
    employeeIdentifier: string
    seniorityDate: string
    teamId: string
    title: string
    effectiveFrom: string
  }
}

export type AccountResult = { ok: true } | { ok: false; message: string }

// A refused request carries its reason in the response body; anything else
// (a network failure, a missing function) reads as the generic fallback.
export async function failure(error: unknown, fallback: string): Promise<AccountResult> {
  const context = (error as { context?: { json?: () => Promise<{ error?: string }> } } | null)?.context
  if (context?.json) {
    try {
      const body = await context.json()
      if (body?.error) return { ok: false, message: body.error }
    } catch {
      // Not a JSON body; fall through to the fallback.
    }
  }
  return { ok: false, message: (error as Error | null)?.message || fallback }
}

async function invoke(name: string, body: Record<string, unknown>, fallback: string): Promise<AccountResult> {
  const { error } = await supabase.functions.invoke(name, { body })
  return error ? failure(error, fallback) : { ok: true }
}

export type TeamResult = { ok: true; team: AccountTeam } | { ok: false; message: string }

// A new team has no supervisor until an account is made to lead it. It joins
// the field office of the teams already listed, so the pilot's one office is
// never a choice the administrator has to make.
export async function createTeam(
  name: string,
  fieldOfficeId?: string,
  dates: { openDate?: string; closeDate?: string } = {},
): Promise<TeamResult> {
  const { data, error } = await supabase.functions.invoke('v1-create-team', { body: { name, fieldOfficeId, ...dates } })
  if (error) {
    const failed = await failure(error, 'The team could not be created.')
    return { ok: false, message: failed.ok ? 'The team could not be created.' : failed.message }
  }
  return { ok: true, team: { id: data.team.id, name: data.team.name, supervisorId: null, fieldOfficeId } }
}

// A team's name and dates. A close date closes it; null reopens it.
export function updateTeam(
  teamId: string,
  values: { name: string; openDate: string; closeDate: string | null },
): Promise<AccountResult> {
  return invoke('v1-update-team', { teamId, ...values }, 'The team could not be updated.')
}

// The supervisor's access to the team's employees begins with this, and the
// previous supervisor's ends.
export function assignTeamSupervisor(teamId: string, supervisorId: string): Promise<AccountResult> {
  return invoke('v1-assign-team-supervisor', { teamId, supervisorId }, 'The supervisor could not be assigned.')
}

export function createAccount(account: NewAccount): Promise<AccountResult> {
  return invoke('v1-create-account', account, 'The account could not be created.')
}

export function updateAccount(
  profileId: string,
  values: { firstName: string; lastName: string; role: AccountRole },
): Promise<AccountResult> {
  return invoke('v1-update-account', { profileId, ...values }, 'The account could not be updated.')
}

export function setAccountActive(profileId: string, active: boolean, reason?: string): Promise<AccountResult> {
  return invoke('v1-set-account-active', { profileId, active, reason }, 'The account could not be changed.')
}
